/**
 * SYR-107 — the research behind a lead, read inline in the queue.
 *
 * Everything research collects has always been stored and nowhere visible: the
 * synthesised prose sits on the subject and the evidence sits on the job, so
 * judging whether a read-out was fair meant running SQL. This is that record,
 * opened from the row it belongs to.
 *
 * Evidence first, prose second, on purpose. The prose is written FROM the
 * evidence, so "41 characters of website" is the thing that explains a generic
 * hypothesis, and it has to be visible before the paragraphs that sound
 * confident regardless.
 */

import React from 'react';
import { Card, Col, Descriptions, Empty, Row, Space, Spin, Tag, Tooltip, Typography } from 'antd';
import { FormattedMessage } from 'react-intl';
import { useGetHypothesisSubjectDetailQuery } from '../../../services/nestjs-base-api';
import { IHypothesisEvidence } from '../../../services/nestjs-base-api/types';

const { Text, Paragraph, Title } = Typography;

interface Props {
    jwt: string;
    subjectId: number | null;
}

// Under this a "page" carried nav furniture and nothing else. Mirrors
// SUBSTANTIVE_PAGE_CHARS on the API, which is what the evidence score counts.
const SUBSTANTIVE_PAGE_CHARS = 200;

const SIGNAL_COLOUR: Record<string, string> = {
    STRENGTH: 'green',
    WATCH: 'gold',
    LIKELY_CONSTRAINT: 'red',
    INSUFFICIENT_SIGNAL: 'default',
};

const section = (
    title: React.ReactNode,
    body: string | null | undefined,
): React.ReactNode => {
    if (!body?.trim()) return null;
    return (
        <div style={{ marginBottom: 16 }}>
            <Text strong style={{ fontSize: 13 }}>
                {title}
            </Text>
            <Paragraph style={{ marginTop: 4, marginBottom: 0, whiteSpace: 'pre-wrap' }}>
                {body}
            </Paragraph>
        </div>
    );
};

// The chips along the top: what was actually fetched, in the order it was
// tried. A reader should be able to tell "thin brand" from "site we could not
// read" without opening the JSON.
const EvidenceChips: React.FC<{ evidence: IHypothesisEvidence }> = ({ evidence }) => {
    const siteChars = (evidence.pagesFetched ?? []).reduce((sum, p) => sum + p.chars, 0);
    const substantive = (evidence.pagesFetched ?? []).filter(
        (p) => p.chars >= SUBSTANTIVE_PAGE_CHARS,
    ).length;

    return (
        <Space size={[6, 6]} wrap style={{ marginBottom: 12 }}>
            <Tooltip
                title={(evidence.pagesFetched ?? [])
                    .map((p) => `${p.url} — ${p.chars.toLocaleString()} chars`)
                    .join('\n')}
            >
                <Tag color={siteChars >= 400 ? 'blue' : 'red'}>
                    <FormattedMessage
                        id="publicHypothesis.admin.research.site"
                        defaultMessage="Website: {chars} chars across {pages} page(s), {substantive} substantive"
                        values={{
                            chars: siteChars.toLocaleString(),
                            pages: evidence.pagesFetched?.length ?? 0,
                            substantive,
                        }}
                    />
                </Tag>
            </Tooltip>

            {evidence.catalogue ? (
                <Tooltip title={evidence.catalogue.url}>
                    <Tag color="geekblue">
                        <FormattedMessage
                            id="publicHypothesis.admin.research.catalogue"
                            defaultMessage="Shopify catalogue: {count} products{excluded}"
                            values={{
                                count: evidence.catalogue.productCount,
                                excluded: evidence.catalogue.excludedCount
                                    ? ` (${evidence.catalogue.excludedCount} placeholder skipped)`
                                    : '',
                            }}
                        />
                    </Tag>
                </Tooltip>
            ) : null}

            {evidence.rendered ? (
                <Tag color="purple">
                    <FormattedMessage
                        id="publicHypothesis.admin.research.rendered"
                        defaultMessage="Rendered via {provider}: {chars} chars"
                        values={{
                            provider: evidence.rendered.provider,
                            chars: evidence.rendered.chars.toLocaleString(),
                        }}
                    />
                </Tag>
            ) : null}

            <Tag color={evidence.place?.found ? 'blue' : 'default'}>
                {evidence.place?.found ? (
                    <FormattedMessage
                        id="publicHypothesis.admin.research.listings"
                        defaultMessage="Google: {count} listing(s), {rating}★ across {reviews} reviews"
                        values={{
                            count: evidence.place.listingCount ?? 1,
                            rating: evidence.place.aggregateRating ?? evidence.place.rating ?? '—',
                            reviews: (
                                evidence.place.aggregateReviewCount ??
                                evidence.place.totalReviews ??
                                0
                            ).toLocaleString(),
                        }}
                    />
                ) : (
                    <FormattedMessage
                        id="publicHypothesis.admin.research.noListing"
                        defaultMessage="No Google listing"
                    />
                )}
            </Tag>

            <Tooltip
                title={(evidence.place?.sampledLocations ?? [])
                    .map((l) => `${l.name} — ${l.reviewsCollected}`)
                    .join('\n')}
            >
                <Tag color={evidence.reviewTextsCollected > 0 ? 'blue' : 'red'}>
                    <FormattedMessage
                        id="publicHypothesis.admin.research.reviews"
                        defaultMessage="{count} review texts ({source}){locations}"
                        values={{
                            count: evidence.reviewTextsCollected ?? 0,
                            source: evidence.reviewSource,
                            locations: evidence.place?.sampledLocations?.length
                                ? `, sampled from ${evidence.place.sampledLocations.length} location(s)`
                                : '',
                        }}
                    />
                </Tag>
            </Tooltip>

            {(evidence.ecosystemSources ?? []).map((e) => (
                <Tooltip key={e.url} title={`${e.url} — ${e.chars.toLocaleString()} chars`}>
                    <Tag color="cyan">{e.source}</Tag>
                </Tooltip>
            ))}

            {evidence.providersDegraded ? (
                <Tooltip title={(evidence.degradedReasons ?? []).join(', ')}>
                    <Tag color="red">
                        <FormattedMessage
                            id="publicHypothesis.admin.research.degraded"
                            defaultMessage="Provider degraded — our outage, not a thin brand"
                        />
                    </Tag>
                </Tooltip>
            ) : null}
        </Space>
    );
};

export const LeadResearchPanel: React.FC<Props> = ({ jwt, subjectId }) => {
    const { data, isFetching } = useGetHypothesisSubjectDetailQuery(
        { jwtToken: jwt, subjectId: subjectId as number },
        { skip: !subjectId },
    );

    if (!subjectId) {
        return (
            <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description={
                    <FormattedMessage
                        id="publicHypothesis.admin.research.noSubject"
                        defaultMessage="No research subject — this lead never started a run."
                    />
                }
            />
        );
    }

    if (isFetching) {
        return (
            <div style={{ padding: 24, textAlign: 'center' }}>
                <Spin />
            </div>
        );
    }

    const subject = data?.subject;
    const evidence = data?.job?.evidenceJson;
    const hypothesis = data?.hypothesis;

    if (!subject) {
        return (
            <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description={
                    <FormattedMessage
                        id="publicHypothesis.admin.research.none"
                        defaultMessage="Nothing collected for this subject yet."
                    />
                }
            />
        );
    }

    return (
        <div style={{ padding: '4px 8px 8px' }}>
            {evidence ? <EvidenceChips evidence={evidence} /> : null}

            {/* The score split, because a run that clears the gate on
                competitor research is exactly what this panel exists to make
                visible. */}
            {data?.job ? (
                <Descriptions size="small" column={4} style={{ marginBottom: 12 }}>
                    {/* A run from before the brand/context split has one
                        combined number that INCLUDED competitor research, so
                        showing it under "Brand evidence" would repeat the
                        confusion this panel exists to end. Label it for what
                        it is instead. */}
                    {evidence?.scoreBreakdown ? (
                        <>
                            <Descriptions.Item
                                label={
                                    <FormattedMessage
                                        id="publicHypothesis.admin.research.brandScore"
                                        defaultMessage="Brand evidence"
                                    />
                                }
                            >
                                <Text strong>{evidence.scoreBreakdown.brand}</Text>
                            </Descriptions.Item>
                            <Descriptions.Item
                                label={
                                    <FormattedMessage
                                        id="publicHypothesis.admin.research.contextScore"
                                        defaultMessage="Competitor context"
                                    />
                                }
                            >
                                {evidence.scoreBreakdown.context}
                            </Descriptions.Item>
                        </>
                    ) : (
                        <Descriptions.Item
                            span={2}
                            label={
                                <FormattedMessage
                                    id="publicHypothesis.admin.research.legacyScore"
                                    defaultMessage="Evidence score"
                                />
                            }
                        >
                            <Tooltip
                                title={
                                    <FormattedMessage
                                        id="publicHypothesis.admin.research.legacyScoreHint"
                                        defaultMessage="Scored before brand and competitor evidence were separated, so this total includes up to 3 points of competitor research."
                                    />
                                }
                            >
                                <Text strong>{data.job.evidenceScore ?? '—'}</Text>
                                <Text type="secondary" style={{ fontSize: 11, marginLeft: 6 }}>
                                    <FormattedMessage
                                        id="publicHypothesis.admin.research.legacyTag"
                                        defaultMessage="(pre-split)"
                                    />
                                </Text>
                            </Tooltip>
                        </Descriptions.Item>
                    )}
                    <Descriptions.Item
                        label={
                            <FormattedMessage
                                id="publicHypothesis.admin.research.jobStatus"
                                defaultMessage="Job"
                            />
                        }
                    >
                        {data.job.status}
                    </Descriptions.Item>
                    <Descriptions.Item
                        label={
                            <FormattedMessage
                                id="publicHypothesis.admin.research.researchedAt"
                                defaultMessage="Researched"
                            />
                        }
                    >
                        {subject.researchedAt
                            ? new Date(subject.researchedAt).toLocaleString()
                            : '—'}
                    </Descriptions.Item>
                </Descriptions>
            ) : null}

            {evidence?.evidenceNotes ? (
                <Card
                    size="small"
                    style={{ marginBottom: 12, background: '#fffbe6', borderColor: '#ffe58f' }}
                >
                    <Text type="secondary" style={{ fontSize: 12 }}>
                        <FormattedMessage
                            id="publicHypothesis.admin.research.notesLabel"
                            defaultMessage="What the model said about its own evidence base"
                        />
                    </Text>
                    <Paragraph style={{ marginBottom: 0, marginTop: 4 }}>
                        {evidence.evidenceNotes}
                    </Paragraph>
                </Card>
            ) : null}

            <Row gutter={16}>
                <Col xs={24} lg={12}>
                    {section(
                        <FormattedMessage
                            id="publicHypothesis.admin.research.footprint"
                            defaultMessage="Public footprint"
                        />,
                        subject.publicFootprint,
                    )}
                    {section(
                        <FormattedMessage
                            id="publicHypothesis.admin.research.sentiment"
                            defaultMessage="Customer sentiment"
                        />,
                        subject.customerSentiment,
                    )}
                    {section(
                        <FormattedMessage
                            id="publicHypothesis.admin.research.landscape"
                            defaultMessage="Competitive landscape"
                        />,
                        subject.competitiveLandscape,
                    )}
                </Col>
                <Col xs={24} lg={12}>
                    {section(
                        <FormattedMessage
                            id="publicHypothesis.admin.research.positioning"
                            defaultMessage="Positioning"
                        />,
                        subject.positioning,
                    )}
                    {section(
                        <FormattedMessage
                            id="publicHypothesis.admin.research.targetCustomers"
                            defaultMessage="Target customers"
                        />,
                        subject.targetCustomers,
                    )}
                    {section(
                        <FormattedMessage
                            id="publicHypothesis.admin.research.products"
                            defaultMessage="Products and services"
                        />,
                        subject.productsServices,
                    )}
                    {section(
                        <FormattedMessage
                            id="publicHypothesis.admin.research.competitors"
                            defaultMessage="Key competitors"
                        />,
                        subject.keyCompetitors,
                    )}
                </Col>
            </Row>

            {hypothesis ? (
                <Card size="small" style={{ marginTop: 4 }}>
                    <Title level={5} style={{ marginTop: 0, marginBottom: 4 }}>
                        {hypothesis.headline}
                    </Title>
                    <Space size={6} wrap style={{ marginBottom: 8 }}>
                        <Tag color="red">{hypothesis.constraintElement}</Tag>
                        {(hypothesis.elementReadings ?? []).map((r) => (
                            <Tooltip key={r.element} title={r.note}>
                                <Tag color={SIGNAL_COLOUR[r.signal] ?? 'default'}>{r.element}</Tag>
                            </Tooltip>
                        ))}
                    </Space>
                    <Paragraph style={{ marginBottom: 4 }}>{hypothesis.claim}</Paragraph>
                    <Text type="secondary" style={{ fontSize: 12 }}>
                        {hypothesis.sourcesSummary}
                    </Text>
                </Card>
            ) : null}
        </div>
    );
};
